'use client';

import { useMemo, type CSSProperties } from 'react';
import { blue, emerald, orange, yellow } from '@/lib/colours';

import { Chip, type ChipProps } from '@/primitives';
import type { SxProps } from '@mui/material/styles';
import { useResolvedColorScheme } from '@/hooks/useResolvedColorScheme';

export enum Sentiment {
  Negative = 'negative',
  Tentative = 'tentative',
  Warning = 'warning',
  Positive = 'positive',
  Neutral = 'neutral',
  Informative = 'informative',
  InProgress = 'inProgress',
}

export const sentimentColorMap: Record<Sentiment, string> = {
  [Sentiment.Negative]: 'error.main',
  [Sentiment.Tentative]: yellow[600],
  [Sentiment.Warning]: orange[600],
  [Sentiment.Positive]: emerald[500],
  [Sentiment.Neutral]: 'text.secondary',
  [Sentiment.Informative]: blue[600],
  [Sentiment.InProgress]: blue[600],
};

export const sentimentBackgroundColorMap: Record<Sentiment, string> = {
  [Sentiment.Negative]: 'error.light',
  [Sentiment.Tentative]: yellow[100],
  [Sentiment.Warning]: orange[100],
  [Sentiment.Positive]: emerald[100],
  [Sentiment.Neutral]: 'action.hover',
  [Sentiment.Informative]: blue[100],
  [Sentiment.InProgress]: blue[100],
};

export interface StatusChipProps extends ChipProps {
  sentiment: Sentiment;
}

/** Inline dark styles: theme.applyStyles / sx lose to MUI Chip’s CSS-variable layer in v7. */
function useDarkSentimentStyle(
  sentiment: Sentiment,
  isDarkMode: boolean,
): CSSProperties | undefined {
  return useMemo(() => {
    if (!isDarkMode) return undefined;
    switch (sentiment) {
      case Sentiment.Neutral:
        return { backgroundColor: '#475569', color: '#f8fafc' };
      case Sentiment.Informative:
      case Sentiment.InProgress:
        return { backgroundColor: '#1e40af', color: '#bfdbfe' };
      default:
        return undefined;
    }
  }, [isDarkMode, sentiment]);
}

const statusChipSx: SxProps = {
  '& .MuiChip-label': { color: 'inherit' },
};

export function StatusChip({
  sentiment,
  sx: sxProp,
  style,
  ...props
}: StatusChipProps) {
  const { isDarkMode } = useResolvedColorScheme();
  const darkStyle = useDarkSentimentStyle(sentiment, isDarkMode);

  return (
    <Chip
      {...props}
      sx={[
        {
          backgroundColor: sentimentBackgroundColorMap[sentiment],
          color: sentimentColorMap[sentiment],
        },
        statusChipSx,
        ...(Array.isArray(sxProp) ? sxProp : sxProp ? [sxProp] : []),
      ]}
      style={{ ...style, ...darkStyle }}
    />
  );
}
